const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/zgks/eCheckin/sections/EinwArzt.tsx';
import * as React from 'react';
import { forwardRef, useEffect, useMemo } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import _ from 'lodash';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { ArztBlock, ArztFieldsConfig } from 'App/components/eCheckin/components/fields/ArztBlock';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { useIntl } from 'react-intl';
import { CIS4mSelect } from '@cistec/common_web/modules/CIS4m/components/CIS4mSelect';
import { OptionEntry } from '@cistec/common_web/modules/CIS4m/types';
import { actionRemoveFromCIS4mDataObject, actionSetInCIS4mDataObject } from '@cistec/common_web/modules/CIS4m/actions';
import { useDispatch } from 'react-redux';
import { useCIS4mContext } from '@cistec/common_web/modules/CIS4m/context';
import { PATH_KEIN_HAUSARZT } from 'App/config/zgks/eCheckin/sections/Hausarzt';
import { SECTION_PATH_EINWARZT } from 'App/config/zgks/eCheckin/constants';

const PATH_EINW_TYPE = createFieldPath(SECTION_PATH_EINWARZT, '_einweisungType');
const PATH_EINWEISUNG_HAUSARZT = createFieldPath(SECTION_PATH_EINWARZT, 'hausArzt');
const PATH_SELBSTEINWEISUNG = createFieldPath(SECTION_PATH_EINWARZT, 'selbstEinweisung');

const valueHausarzt = 'Hausarzt';
const valueSelbsteinweisung = 'selbsteinweisung';
const valueAndere = 'andere';

export const EinwArtz = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const { formatMessage } = useIntl();
    const dispatch = useDispatch();

    const arztFieldsConfig: ArztFieldsConfig = {
        zusatz: { included: true, helperText: formatMessage(textMap.zusatzHelpText) },
    };

    const formData = useCIS4mData();
    const { formId } = useCIS4mContext();

    const keinHausarzt = _.get(formData, PATH_KEIN_HAUSARZT) === '1';
    const hasHausarzt = !keinHausarzt;

    const selectedEinwType = _.get(formData, PATH_EINW_TYPE);

    const isEinweisungHausarzt = _.get(formData, PATH_EINWEISUNG_HAUSARZT) === '1';
    const isSelbsteinweisung = _.get(formData, PATH_SELBSTEINWEISUNG) === '1';
    const isEinweisungAndere = selectedEinwType === valueAndere;

    const arztBlockHidden = !isEinweisungAndere;

    const einwTypeOptions: OptionEntry[] = useMemo(
        () =>
            _.compact([
                hasHausarzt ? { label: formatMessage(textMap.hausArzt), value: valueHausarzt } : null,
                { label: formatMessage(textMap.selbstEinweisung), value: valueSelbsteinweisung },
                { label: formatMessage(textMap.andereArzt), value: valueAndere },
            ]),
        [hasHausarzt]
    );

    useEffect(() => {
        if (!hasHausarzt) {
            if (isEinweisungHausarzt) {
                dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_EINW_TYPE));
            }
            dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_EINWEISUNG_HAUSARZT));
        }
    }, [hasHausarzt]);

    useEffect(() => {
        if (selectedEinwType === valueHausarzt && hasHausarzt) {
            dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_SELBSTEINWEISUNG));
            dispatch(actionSetInCIS4mDataObject(formId, PATH_EINWEISUNG_HAUSARZT, '1'));
        } else if (selectedEinwType === valueAndere) {
            dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_SELBSTEINWEISUNG));
            dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_EINWEISUNG_HAUSARZT));
        } else if (selectedEinwType === valueSelbsteinweisung) {
            dispatch(actionRemoveFromCIS4mDataObject(formId, PATH_EINWEISUNG_HAUSARZT));
            dispatch(actionSetInCIS4mDataObject(formId, PATH_SELBSTEINWEISUNG, '1'));
        }
    }, [selectedEinwType]);

    const eCheckinSectionRef = useCIS4m(ref);
    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <CIS4mSelect label={textMap.arztType} path={PATH_EINW_TYPE} options={einwTypeOptions} required={true} />
            <ArztBlock
                basePath={path}
                hidden={arztBlockHidden}
                // in old version, all fields are optional...
                required={false}
                fieldsConfig={arztFieldsConfig}
            />
        </ECheckinSection>
    );
});

EinwArtz.displayName = 'EinwArtz';

export default EinwArtz;
